import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiArrowRight, FiCalendar, FiMapPin, FiMessageCircle, FiSearch } from "react-icons/fi";
import { heroContent, heroStats } from "./homePreviewData.js";

const easeOut = [0.22, 1, 0.36, 1];

const containerStagger = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.12, delayChildren: 0.08 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: easeOut } },
};

const titleWords = heroContent.title.split(" ");

export default function HomePreviewHero() {
  return (
    <section
      id="preview-hero"
      className="relative isolate overflow-hidden bg-[linear-gradient(135deg,#FDFBF7_0%,#F5F0E8_58%,#eee2c4_100%)]"
    >
      <motion.div
        initial="hidden"
        animate="show"
        variants={containerStagger}
        className="relative mx-auto grid max-w-7xl gap-8 px-4 pb-12 pt-8 sm:px-6 sm:pb-16 sm:pt-12 lg:min-h-[650px] lg:grid-cols-[0.88fr_1.12fr] lg:items-center"
      >
        <div className="max-w-2xl">
          <motion.p
            variants={fadeUp}
            className="inline-flex rounded-full border border-zoom-gold/45 bg-white/85 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-zoom-emeraldDeep shadow-hp-xs backdrop-blur"
          >
            {heroContent.eyebrow}
          </motion.p>

          <h1 className="font-display mt-5 text-4xl font-bold leading-[1.05] tracking-tight text-zoom-emeraldDeep sm:text-5xl lg:text-[62px]">
            {titleWords.map((word, index) => (
              <motion.span
                key={`${word}-${index}`}
                variants={fadeUp}
                className="mr-[0.28em] inline-block last:mr-0"
              >
                {word}
              </motion.span>
            ))}
          </h1>

          <motion.p variants={fadeUp} className="mt-5 max-w-xl text-base font-normal leading-8 text-zoom-charcoal/72 sm:text-lg">
            {heroContent.subtitle}
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/banquets"
              className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-zoom-emeraldDeep px-6 py-3 text-sm font-semibold text-white shadow-hp-emerald transition hover:-translate-y-0.5 hover:bg-zoom-emerald"
            >
              Search Banquets <FiArrowRight aria-hidden="true" />
            </Link>
            <a
              href="#preview-services"
              className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-zoom-emerald/30 bg-white/85 px-6 py-3 text-sm font-semibold text-zoom-emeraldDeep shadow-hp-xs backdrop-blur transition hover:-translate-y-0.5 hover:border-zoom-gold"
            >
              Explore Decorations <FiSearch aria-hidden="true" />
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-8 grid grid-cols-3 overflow-hidden rounded-3xl border border-zoom-emerald/10 bg-white/82 shadow-hp-md backdrop-blur"
          >
            {heroStats.map((stat) => (
              <div key={stat.label} className="border-r border-zoom-emerald/10 p-3 last:border-r-0 sm:p-5">
                <p className="font-display text-xl font-bold text-zoom-emeraldDeep sm:text-3xl">{stat.value}</p>
                <p className="mt-1 text-[11px] font-medium uppercase leading-5 tracking-[0.12em] text-zoom-charcoal/62 sm:text-xs">
                  {stat.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          variants={fadeUp}
          className="relative min-h-[460px] lg:min-h-[570px]"
        >
          <div className="absolute left-0 right-0 top-2 overflow-hidden rounded-[32px] border border-white/70 bg-white shadow-hp-xl sm:left-10 lg:top-0">
            <img
              src={heroContent.image}
              alt="Premium DecorZoom banquet and event setup"
              fetchPriority="high"
              onError={(event) => {
                event.currentTarget.src = heroContent.fallbackImage;
              }}
              className="h-[420px] w-full object-cover sm:h-[520px] lg:h-[570px]"
            />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,46,33,0)_42%,rgba(5,46,33,0.64)_100%)]" />
            <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.26em] text-zoom-goldSoft">Banquet + Decor Planning</p>
              <h3 className="font-display mt-3 max-w-xl text-2xl font-semibold leading-tight text-white sm:text-4xl">
                Birthday, baby shower, wedding and family event specialists.
              </h3>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: -14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55, ease: easeOut }}
            className="absolute left-0 top-10 hidden w-56 rounded-3xl border border-white/70 bg-white/92 p-4 shadow-hp-md backdrop-blur sm:block"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-zoom-goldSoft/60 text-zoom-emeraldDeep">
                <FiMapPin aria-hidden="true" />
              </span>
              <div>
                <p className="text-sm font-semibold text-zoom-emeraldDeep">Delhi NCR</p>
                <p className="text-xs font-medium text-zoom-charcoal/60">venue shortlist</p>
              </div>
            </div>
          </motion.div>

          <div className="absolute left-3 right-3 top-1/2 -translate-y-1/2 sm:left-auto sm:right-8 sm:w-80">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.65, ease: easeOut }}
              className="rounded-3xl border border-zoom-gold/50 bg-white/94 p-4 shadow-hp-lg backdrop-blur"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-zoom-gold">Quick WhatsApp support</p>
                  <p className="mt-2 text-lg font-semibold leading-tight text-zoom-emeraldDeep">Tell us guests, date, and vibe.</p>
                </div>
                <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-zoom-emeraldDeep text-white">
                  <FiMessageCircle aria-hidden="true" />
                </span>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs font-medium text-zoom-charcoal/72">
                <span className="rounded-full bg-zoom-emerald/10 px-3 py-2">Banquet</span>
                <span className="rounded-full bg-zoom-goldSoft/45 px-3 py-2">Decor</span>
                <span className="rounded-full bg-zoom-goldSoft/45 px-3 py-2">Activities</span>
                <span className="rounded-full bg-zoom-emerald/10 px-3 py-2">Gifts</span>
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.75, ease: easeOut }}
            className="absolute right-2 top-4 rounded-full border border-white/50 bg-white/88 px-4 py-3 text-sm font-semibold text-zoom-emeraldDeep shadow-hp-sm backdrop-blur sm:right-5"
          >
            <span className="inline-flex items-center gap-2">
              <FiCalendar aria-hidden="true" /> Event-ready
            </span>
          </motion.div>
        </motion.div>
      </motion.div>
    </section>
  );
}
